import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, HelpCircle, FileText, ChevronDown } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Comment prendre un rendez-vous ?",
    a: "Recherchez un praticien par spécialité ou par ville depuis la page d'accueil, ouvrez sa fiche, choisissez un créneau disponible et confirmez. Vous recevez immédiatement un email de confirmation.",
  },
  {
    q: "Comment annuler ou modifier un rendez-vous ?",
    a: "Connectez-vous à votre compte, ouvrez l'onglet « Mes rendez-vous », puis cliquez sur le bouton « Annuler » à côté du RDV concerné. Pour modifier, annulez puis reprenez un nouveau créneau. Le praticien est notifié automatiquement.",
  },
  {
    q: "Je n'arrive plus à me connecter, que faire ?",
    a: "Cliquez sur « Mot de passe oublié » sur la page de connexion. Vous recevrez un email avec un lien sécurisé pour le réinitialiser. Si l'email n'arrive pas, vérifiez vos spams puis écrivez-nous.",
  },
  {
    q: "Je suis praticien, comment m'inscrire ?",
    a: "Rendez-vous sur la page d'inscription praticien et remplissez le formulaire avec votre numéro RPPS. Votre compte est ensuite vérifié par notre équipe sous 48h ouvrées avant que votre profil ne soit visible des patients.",
  },
  {
    q: "Comment contacter mon praticien après un RDV ?",
    a: "Une messagerie sécurisée est disponible : ouvrez le RDV concerné depuis « Mes rendez-vous » et cliquez sur le bouton « Message ». Vous pouvez échanger pendant 30 jours après la consultation.",
  },
  {
    q: "Quelles données sont collectées ?",
    a: "Nous collectons uniquement les informations nécessaires à la prise de rendez-vous (email, nom, téléphone, motif). Toutes vos données sont hébergées dans l'Union européenne et conformes au RGPD. Voir notre Politique de confidentialité.",
  },
  {
    q: "Comment supprimer mon compte ?",
    a: "Envoyez-nous un email à contact@doctodays.com depuis l'adresse associée à votre compte. Nous procédons à la suppression de toutes vos données personnelles sous 30 jours, conformément à votre droit à l'effacement (RGPD).",
  },
];

function FaqItem({ item, open, onToggle }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`faq-${item.q.slice(0, 20)}`}
      >
        <span className="font-medium text-slate-800">{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-slate-600 leading-relaxed border-t border-slate-100">
          <p className="pt-3">{item.a}</p>
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-emerald-600 font-medium hover:underline" data-testid="back-home">
          ← Retour à l'accueil
        </Link>

        <h1 className="text-4xl font-bold text-slate-900 mt-6 mb-2">Aide et support</h1>
        <p className="text-slate-600 mb-10">
          Une question ? Un problème ? Nous sommes là pour vous aider.
        </p>

        {/* Contact cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          <a
            href="mailto:contact@doctodays.com"
            className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-md transition-all"
            data-testid="support-contact-email"
          >
            <Mail className="w-6 h-6 text-emerald-600 mb-2" />
            <h3 className="font-semibold text-slate-900 mb-1">Nous écrire</h3>
            <p className="text-slate-500 text-sm mb-2">Réponse sous 24-48h ouvrées</p>
            <p className="text-emerald-600 font-medium text-sm">contact@doctodays.com</p>
          </a>
          <Link
            to="/messages"
            className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-md transition-all"
            data-testid="support-contact-chat"
          >
            <MessageCircle className="w-6 h-6 text-emerald-600 mb-2" />
            <h3 className="font-semibold text-slate-900 mb-1">Contacter mon praticien</h3>
            <p className="text-slate-500 text-sm">Messagerie sécurisée disponible 30 jours après chaque RDV.</p>
          </Link>
        </div>

        {/* FAQ */}
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-6 h-6 text-emerald-600" />
          <h2 className="text-2xl font-bold text-slate-900">Questions fréquentes</h2>
        </div>
        <div className="space-y-3 mb-12">
          {FAQ_ITEMS.map((item, idx) => (
            <FaqItem
              key={idx}
              item={item}
              open={openIdx === idx}
              onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
            />
          ))}
        </div>

        {/* Useful links */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Documents utiles</h3>
          </div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/privacy" className="text-emerald-600 hover:underline" data-testid="support-link-privacy">
                Politique de confidentialité
              </Link>
            </li>
            <li>
              <Link to="/terms" className="text-emerald-600 hover:underline" data-testid="support-link-terms">
                Conditions générales d'utilisation
              </Link>
            </li>
          </ul>
        </div>

        <p className="text-center text-slate-400 text-sm mt-10">
          DoctoDays — Plateforme de prise de rendez-vous médicaux
        </p>
      </div>
    </div>
  );
}
